import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { API_URLS } from '../config/api';

const TIME_SLOTS = (() => {
  const slots = [];
  let hour = 10;
  let minute = 0;
  while (hour < 21 || (hour === 21 && minute === 0)) {
    const h = hour.toString().padStart(2, '0');
    const m = minute.toString().padStart(2, '0');
    slots.push(`${h}:${m}`);
    minute += 30;
    if (minute >= 60) {
      minute = 0;
      hour += 1;
    }
  }
  return slots;
})();

const formatDateISO = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Seçilen gün bugünse ve slot saati şu anı geçmişse true */
const isSlotInPast = (dateISO, timeSlot) => {
  if (!dateISO || !timeSlot) return false;
  const today = formatDateISO(new Date());
  if (dateISO !== today) return false;
  const [h, m] = timeSlot.split(':').map(Number);
  const slotAt = new Date();
  slotAt.setHours(h, m, 0, 0);
  return new Date() >= slotAt;
};

const buildMonthMatrix = (year, monthIndex) => {
  // monthIndex: 0-11
  const firstDay = new Date(year, monthIndex, 1);
  const firstWeekday = firstDay.getDay(); // 0=Sun
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const matrix = [];
  let currentDay = 1;
  // 0 (Paz) -> 6 (Cmt), but grid is 7x6
  for (let week = 0; week < 6; week++) {
    const row = [];
    for (let wd = 0; wd < 7; wd++) {
      const cellIndex = week * 7 + wd;
      if (cellIndex < firstWeekday || currentDay > daysInMonth) {
        row.push(null);
      } else {
        row.push(currentDay);
        currentDay += 1;
      }
    }
    matrix.push(row);
  }
  return matrix;
};

export default function MeetingRequestDialog({ open, onOpenChange, context }) {
  const today = new Date();
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    meeting_date: formatDateISO(today),
    meeting_time: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [takenSlots, setTakenSlots] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(
    form.meeting_date
      ? new Date(form.meeting_date)
      : new Date(today.getFullYear(), today.getMonth(), 1)
  );

  useEffect(() => {
    if (!form.meeting_date) {
      setTakenSlots([]);
      return;
    }
    let cancelled = false;
    fetch(API_URLS.MEETING_SLOTS_BY_DATE(form.meeting_date))
      .then((res) => res.ok ? res.json() : [])
      .then((data) => {
        if (!cancelled && Array.isArray(data)) {
          setTakenSlots(data);
          if (form.meeting_time && data.includes(form.meeting_time)) {
            setForm((prev) => ({ ...prev, meeting_time: '' }));
          }
        }
      })
      .catch(() => { if (!cancelled) setTakenSlots([]); });
    return () => { cancelled = true; };
  }, [form.meeting_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.customer_phone || !form.meeting_date || !form.meeting_time) {
      alert('Lütfen ad, e-posta, telefon, tarih ve saat alanlarını doldurun.');
      return;
    }
    if (isSlotInPast(form.meeting_date, form.meeting_time)) {
      alert('Seçtiğiniz saat geçmişte. Lütfen ileri bir saat seçin.');
      return;
    }
    if (takenSlots.includes(form.meeting_time)) {
      alert('Bu saat için zaten bir toplantı talebi var. Lütfen başka bir saat seçin.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        meeting_date: form.meeting_date,
        meeting_time: form.meeting_time,
        notes: form.notes || null,
        source: context || null
      };

      const res = await fetch(API_URLS.MEETING_REQUESTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        if (res.status === 409) {
          const data = await res.json().catch(() => null);
          const msg = data?.message || 'Seçtiğiniz tarih ve saatte zaten bir toplantı var. Lütfen başka bir saat seçin.';
          alert(msg);
          return;
        }
        throw new Error('Toplantı talebi gönderilemedi');
      }

      setSubmitted(true);
      setForm({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        meeting_date: '',
        meeting_time: '',
        notes: ''
      });

      setTimeout(() => {
        setSubmitted(false);
        onOpenChange(false);
      }, 1500);
    } catch (error) {
      console.error('Error submitting meeting request:', error);
      alert('Toplantı talebiniz gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Toplantı Ayarla</DialogTitle>
          <DialogDescription>
            İsim, iletişim ve size uygun gün & saati bırakın; ekibimiz kısa sürede dönüş yapsın.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="meeting-name">Ad Soyad</Label>
              <Input
                id="meeting-name"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="meeting-phone">Telefon</Label>
              <Input
                id="meeting-phone"
                value={form.customer_phone}
                onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="meeting-email">E-posta</Label>
              <Input
                id="meeting-email"
                type="email"
                value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="mb-1 block">Tarih</Label>
              <div className="border rounded-lg p-3 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <button
                    type="button"
                    className="px-2 py-1 text-xs rounded-md border border-gray-300 hover:bg-gray-100"
                    onClick={() => {
                      setCurrentMonth(
                        (prev) =>
                          new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                      );
                    }}
                  >
                    Önceki
                  </button>
                  <div className="text-xs font-medium text-gray-800">
                    {currentMonth.toLocaleDateString('tr-TR', {
                      month: 'long',
                      year: 'numeric'
                    })}
                  </div>
                  <button
                    type="button"
                    className="px-2 py-1 text-xs rounded-md border border-gray-300 hover:bg-gray-100"
                    onClick={() => {
                      setCurrentMonth(
                        (prev) =>
                          new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                      );
                    }}
                  >
                    Sonraki
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-[10px] text-gray-500 mb-1">
                  {['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'].map((d) => (
                    <div key={d} className="text-center">
                      {d}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {buildMonthMatrix(
                    currentMonth.getFullYear(),
                    currentMonth.getMonth()
                  ).map((week, wi) =>
                    week.map((day, di) => {
                      if (!day) {
                        return <div key={`${wi}-${di}`} />;
                      }
                      const dateObj = new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth(),
                        day
                      );
                      const iso = formatDateISO(dateObj);
                      const isSelected = form.meeting_date === iso;
                      const isPast = dateObj < new Date(today.toDateString());
                      return (
                        <button
                          key={`${wi}-${di}`}
                          type="button"
                          disabled={isPast}
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              meeting_date: iso
                            }))
                          }
                          className={`h-8 text-xs rounded-md border transition-all ${
                            isSelected
                              ? 'bg-[#7A5AFF] text-white border-[#7A5AFF] shadow'
                              : isPast
                              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                              : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'
                          }`}
                        >
                          {day}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            <div>
              <Label className="mb-1 block">Saat Seçimi</Label>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {TIME_SLOTS.map((slot) => {
                  const isPast = isSlotInPast(form.meeting_date, slot);
                  const isTaken = takenSlots.includes(slot);
                  const disabled = isPast || isTaken;
                  const isActive = form.meeting_time === slot;
                  return (
                    <button
                      key={slot}
                      type="button"
                      disabled={disabled}
                      onClick={() => !disabled && setForm({ ...form, meeting_time: slot })}
                      title={isTaken ? 'Bu saat dolu' : undefined}
                      className={`text-xs sm:text-sm px-2 py-1.5 rounded-md border transition-all ${
                        isPast
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : isTaken
                            ? 'bg-red-50 text-red-500 border-red-200 cursor-not-allowed'
                            : isActive
                              ? 'bg-[#7A5AFF] text-white border-[#7A5AFF] shadow'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      <span className="block">{slot}</span>
                      {isTaken && (
                        <span className="block mt-0.5 text-[10px] leading-none">
                          dolu
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="meeting-notes">Not (opsiyonel)</Label>
            <Textarea
              id="meeting-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Görüşmek istediğiniz konuyu kısaca yazabilirsiniz."
            />
          </div>
          <DialogFooter className="flex flex-col sm:flex-row sm:justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Vazgeç
            </Button>
            <Button
              type="submit"
              className="bg-[#7A5AFF] hover:bg-[#8b6bff] text-white"
              disabled={submitting}
            >
              {submitting ? 'Gönderiliyor...' : submitted ? 'Gönderildi' : 'Toplantı Talebi Gönder'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

